/**
 * ItemSuggestInput — predictive line-item entry for the Sourcing Desk.
 *
 * Type a SKU or part of a description and it searches what this network has
 * already requested (price and UoM included, because those are the fields
 * people get wrong) and then the marketplace catalogue. Picking a suggestion
 * fills the whole line.
 *
 * It stays a plain controlled input: typing freely is still the fast path, the
 * dropdown is only an offer.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { History, Package, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { API } from "@/App";

export const ItemSuggestInput = ({
  value,
  onChange,          // (text) => void — plain typing
  onPick,            // (suggestion) => void — fills the line
  placeholder,
  className = "",
  testId,
  token,
}) => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef(null);
  const skipRef = useRef(false);   // suppress the fetch triggered by a pick

  useEffect(() => {
    const term = (value || "").trim();
    if (skipRef.current) { skipRef.current = false; return; }
    if (term.length < 2) { setRows([]); setOpen(false); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.get(
          `${API}/admin/sourcing/item-suggest?q=${encodeURIComponent(term)}&limit=8`,
          { headers: { Authorization: `Bearer ${token}` } });
        if (cancelled) return;
        setRows(r.data?.suggestions || []);
        setOpen((r.data?.suggestions || []).length > 0);
        setActive(-1);
      } catch {
        if (!cancelled) { setRows([]); setOpen(false); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [value, token]);

  useEffect(() => {
    const away = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);

  const pick = (s) => {
    skipRef.current = true;
    setOpen(false);
    setRows([]);
    onPick?.(s);
  };

  const onKeyDown = (e) => {
    if (!open || !rows.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive(i => Math.min(i + 1, rows.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive(i => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); pick(rows[active]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="relative" ref={boxRef}>
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => rows.length && setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
        data-testid={testId}
      />
      {loading ? (
        <Loader2 className="w-3 h-3 animate-spin absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
      ) : null}

      {open && rows.length ? (
        <ul
          className="absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-md border border-slate-300 bg-white shadow-lg min-w-[320px]"
          data-testid={`${testId}-suggestions`}
        >
          {rows.map((s, i) => (
            <li key={`${s.sku}-${s.name}-${i}`}>
              <button
                type="button"
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(s)}
                className={`w-full text-left px-2.5 py-1.5 ${i === active ? "bg-cyan-50" : "hover:bg-slate-50"}`}
                data-testid={`${testId}-suggestion-${i}`}
              >
                <p className="text-[12px] font-semibold text-slate-900 leading-tight flex items-start gap-1.5">
                  {s.source === "past_order"
                    ? <History className="w-3 h-3 mt-0.5 text-emerald-600 shrink-0" />
                    : <Package className="w-3 h-3 mt-0.5 text-slate-400 shrink-0" />}
                  <span className="truncate">{s.name || s.sku}</span>
                </p>
                <p className="text-[10px] text-slate-500 pl-4.5">
                  {[s.sku && `SKU ${s.sku}`, s.uom, s.unit_price != null && `$${s.unit_price.toFixed(2)}`,
                    s.manufacturer]
                    .filter(Boolean).join(" · ")}
                </p>
                <p className="text-[10px] pl-4.5">
                  {s.source === "past_order" ? (
                    <span className="text-emerald-700">
                      Ordered before{s.times_requested > 1 ? ` ×${s.times_requested}` : ""}
                      {s.last_hospital ? ` · last for ${s.last_hospital}` : ""}
                    </span>
                  ) : (
                    <span className="text-slate-400">Marketplace catalogue</span>
                  )}
                </p>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export default ItemSuggestInput;
